import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Fab,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Skeleton,
  TextField,
  Typography,
  Pagination,
  Stack,
  Badge,
  useMediaQuery,
  Container,
  Drawer,
  AppBar,
  Toolbar,
  Slide,
  Collapse,
  Tooltip,
} from '@mui/material';
import { 
  SearchRounded, 
  PersonAdd,
  Menu as MenuIcon,
  Close as CloseIcon,
  People,
  CheckCircle,
  Schedule,
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material';
import { Link, useOutlet, useParams, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useTheme, alpha } from '@mui/material/styles';
import API from '../../API';
import { handleAvatarError } from '../../Utils';
import profilePhoto from '../../Components/globalAssets/ppfLogo.png';

const CreatorCard = ({ creator, currentCreator }) => {
  const theme = useTheme();
  const isSelected = creator.creator === currentCreator;
  
  // Safe string extraction for avatar fallback
  const getCreatorInitial = (creator) => {
    if (typeof creator === 'string' && creator.length > 0) {
      return creator.charAt(0).toUpperCase();
    }
    return '?';
  };

  return (
    <ListItemButton
      component={Link}
      to={creator.creator}
      selected={isSelected}
      sx={{
        borderRadius: 2,
        mb: 1,
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: alpha(theme.palette.primary.main, 0.08),
        },
        '&.Mui-selected': {
          backgroundColor: alpha(theme.palette.primary.main, 0.12),
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.16),
          }
        }
      }}
    >
      <Badge 
        overlap="circular"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        badgeContent={
          creator.status === 'active' ? 
            <CheckCircle sx={{ color: 'success.main', fontSize: 16 }} /> :
            <Schedule sx={{ color: 'warning.main', fontSize: 16 }} />
        }
      >
        <Avatar
          src={creator.pfphref || profilePhoto}
          onError={handleAvatarError(profilePhoto)}
          sx={{ mr: 2, width: 48, height: 48 }}
        >
          {getCreatorInitial(creator.creator)}
        </Avatar>
      </Badge>
      <ListItemText
        primary={
          <Typography variant="subtitle1" fontWeight={600}>
            {creator.display_name || creator.creator || 'Unknown Creator'}
          </Typography>
        }
        secondary={
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              @{creator.creator || 'unknown'}
            </Typography>
            {creator.status && (
              <Chip 
                size="small" 
                label={creator.status}
                color={creator.status === 'active' ? 'success' : 'default'}
                variant="outlined"
              />
            )}
          </Stack>
        }
      />
    </ListItemButton>
  );
};

const CreatorSkeleton = () => {
  return (
    <Box sx={{ p: 2, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Skeleton variant="circular" width={48} height={48} sx={{ mr: 2 }} />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="60%" />
          <Skeleton variant="text" width="40%" />
        </Box>
      </Box>
    </Box>
  );
};

// Custom hook for debounced search
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Collapsed Avatar View Component
const CollapsedCreatorItem = ({ creator, currentCreator, theme }) => {
  const isSelected = creator.creator === currentCreator;
  
  const getCreatorInitial = (creator) => {
    if (typeof creator === 'string' && creator.length > 0) {
      return creator.charAt(0).toUpperCase();
    }
    return '?';
  };

  return (
    <Tooltip 
      title={
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {creator.display_name || creator.creator}
          </Typography>
          <Typography variant="caption" color="inherit" sx={{ opacity: 0.8 }}>
            @{creator.creator}
          </Typography>
        </Box>
      } 
      placement="right"
      arrow
    >
      <ListItemButton
        component={Link}
        to={creator.creator}
        selected={isSelected}
        sx={{
          borderRadius: 2,
          mb: 1,
          display: 'flex',
          justifyContent: 'center',
          py: 1.5,
          transition: 'all 0.2s ease',
          position: 'relative',
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
          },
          '&.Mui-selected': {
            backgroundColor: alpha(theme.palette.primary.main, 0.12),
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 4,
              height: '60%',
              backgroundColor: theme.palette.primary.main,
              borderRadius: '0 4px 4px 0',
            },
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.16),
            }
          }
        }}
      >
        <Badge 
          overlap="circular"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          badgeContent={
            creator.status === 'active' ? 
              <CheckCircle sx={{ color: 'success.main', fontSize: 12 }} /> :
              <Schedule sx={{ color: 'warning.main', fontSize: 12 }} />
          }
        >
          <Avatar
            src={creator.pfphref || profilePhoto}
            onError={handleAvatarError(profilePhoto)}
            sx={{ 
              width: 44, 
              height: 44,
              border: isSelected ? `2px solid ${theme.palette.primary.main}` : 'none',
            }}
          >
            {getCreatorInitial(creator.creator)}
          </Avatar>
        </Badge>
      </ListItemButton>
    </Tooltip>
  );
};

// Extracted CreatorsList component
const CreatorsList = ({
  searchInput,
  handleSearchChange,
  filteredCreators,
  creators,
  debouncedSearch,
  isLoading,
  paginatedCreators,
  creatorId,
  page,
  setPage,
  itemsPerPage,
  isCollapsed,
  theme
}) => {
  if (isCollapsed) {
    // Collapsed view - show only avatars
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header with count */}
        <Box sx={{ p: 2, textAlign: 'center', borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Chip 
            icon={<People />} 
            label={filteredCreators.length} 
            size="small"
            color="primary"
            variant="outlined"
          />
        </Box>

        {/* Avatars List */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 1.5 }}>
          {isLoading ? (
            <List sx={{ py: 0 }}>
              {Array.from({ length: 8 }).map((_, i) => (
                <Box key={i} sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                  <Skeleton variant="circular" width={44} height={44} />
                </Box>
              ))}
            </List>
          ) : (
            <List sx={{ py: 0 }}>
              {filteredCreators.map(creator => (
                <CollapsedCreatorItem
                  key={creator.creator}
                  creator={creator}
                  currentCreator={creatorId}
                  theme={theme}
                />
              ))}
            </List>
          )}
        </Box>
      </Box>
    );
  }

  // Expanded view
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 2, pb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            Creators
          </Typography>
          <Chip 
            label={filteredCreators.length} 
            size="small"
            color="primary"
            variant="outlined"
          />
        </Box>
        
        <TextField
          value={searchInput}
          onChange={handleSearchChange}
          variant="outlined"
          fullWidth
          size="small"
          placeholder="Search creators..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRounded />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 3,
              backgroundColor: alpha(theme.palette.background.default, 0.5),
            }
          }}
        />

        {debouncedSearch && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Filtered from {creators?.length || 0} total
          </Typography>
        )}
      </Box>

      <Divider />

      {/* Creators List */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {isLoading ? (
          <List>
            {Array.from({ length: 8 }).map((_, i) => (
              <CreatorSkeleton key={i} />
            ))}
          </List>
        ) : filteredCreators.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <People sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No creators found
            </Typography>
            <Typography variant="body2" color="text.disabled">
              {debouncedSearch ? 'Try adjusting your search terms' : 'Add your first creator to get started'}
            </Typography>
          </Box>
        ) : (
          <List sx={{ py: 0 }}>
            {paginatedCreators.map(creator => (
              <CreatorCard
                key={creator.creator}
                creator={creator}
                currentCreator={creatorId}
              />
            ))}
          </List>
        )}
      </Box>

      {/* Pagination */}
      {filteredCreators.length > itemsPerPage && (
        <>
          <Divider />
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
            <Pagination
              count={Math.ceil(filteredCreators.length / itemsPerPage)}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
              size="small"
              showFirstButton
              showLastButton
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default function AgencyCreators() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { creatorId } = useParams();
  const outlet = useOutlet();
  const navigate = useNavigate();
  
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  
  const itemsPerPage = isMobile ? 12 : 20;

  // Debounce search input
  const debouncedSearch = useDebounce(searchInput, 300);

  const { data: creators, isLoading } = useQuery({
    queryKey: ['agency', 'creators'],
    queryFn: () => API.creators.list({ only_agency: true }),
    refetchOnWindowFocus: false
  });

  // Auto-collapse sidebar when a creator is selected on desktop
  useEffect(() => {
    if (!isMobile && creatorId && !sidebarCollapsed) {
      setSidebarCollapsed(true);
    }
  }, [creatorId, isMobile]);

  // Reset page when search changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const filteredCreators = useMemo(() => {
    if (!creators) return [];
    if (!debouncedSearch) return creators;
    
    const searchLower = debouncedSearch.toLowerCase();
    return creators.filter(creator => {
      const creatorName = (creator.creator || '').toLowerCase();
      const displayName = (creator.display_name || '').toLowerCase();
      const status = (creator.status || '').toLowerCase();
      
      return creatorName.includes(searchLower) || 
             displayName.includes(searchLower) || 
             status.includes(searchLower);
    });
  }, [creators, debouncedSearch]);

  const paginatedCreators = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    return filteredCreators.slice(start, start + itemsPerPage);
  }, [filteredCreators, page, itemsPerPage]);

  const handleSearchChange = useCallback((e) => {
    setSearchInput(e.target.value);
  }, []);

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  // Mobile layout
  if (isMobile) {
    return (
      <Box sx={{ 
        height: '100vh', 
        display: 'flex', 
        flexDirection: 'column',
        pt: 'env(safe-area-inset-top, 0px)'
      }}>
        {/* Mobile Header */}
        <AppBar position="static" elevation={1} color="default">
          <Toolbar>
            <IconButton 
              edge="start" 
              onClick={() => setDrawerOpen(true)}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Creators {filteredCreators.length > 0 && `(${filteredCreators.length})`}
            </Typography>
          </Toolbar>
        </AppBar>

        {/* Mobile Drawer */}
        <Drawer
          anchor="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          PaperProps={{ sx: { width: '85vw', maxWidth: 400 } }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Creators
            </Typography>
            <IconButton onClick={() => setDrawerOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ height: '100%', overflow: 'hidden' }}>
            <CreatorsList
              searchInput={searchInput}
              handleSearchChange={handleSearchChange}
              filteredCreators={filteredCreators}
              creators={creators}
              debouncedSearch={debouncedSearch}
              isLoading={isLoading}
              paginatedCreators={paginatedCreators}
              creatorId={creatorId}
              page={page}
              setPage={setPage}
              itemsPerPage={itemsPerPage}
              isCollapsed={false}
              theme={theme}
            />
          </Box>
        </Drawer>

        {/* Main Content */}
        <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
          {outlet ? (
            outlet
          ) : (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%',
              textAlign: 'center',
              p: 4
            }}>
              <Box>
                <People sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h5" color="text.secondary" gutterBottom>
                  Select a Creator
                </Typography>
                <Typography variant="body1" color="text.disabled">
                  Tap the menu to browse your creators
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        {/* Add Creator FAB */}
        <Fab
          color="primary"
          sx={{ 
            position: 'fixed', 
            bottom: 'calc(16px + env(safe-area-inset-bottom, 0px))', 
            right: 16 
          }}
          onClick={() => navigate('/agency/creators/new')}
        >
          <PersonAdd />
        </Fab>
      </Box>
    );
  }

  // Desktop layout with collapsible sidebar
  return (
    <Container maxWidth="xl" sx={{ height: '100vh', py: 2 }}>
      <Box sx={{ display: 'flex', height: '100%', gap: 2, position: 'relative' }}>
        {/* Collapsible Creators List Panel */}
        <Paper 
          sx={{ 
            width: sidebarCollapsed ? 80 : 400, 
            display: 'flex', 
            flexDirection: 'column',
            transition: 'width 0.3s ease',
            position: 'relative',
            overflow: 'hidden',
          }} 
          elevation={2}
        >
          <CreatorsList
            searchInput={searchInput}
            handleSearchChange={handleSearchChange}
            filteredCreators={filteredCreators}
            creators={creators}
            debouncedSearch={debouncedSearch}
            isLoading={isLoading}
            paginatedCreators={paginatedCreators}
            creatorId={creatorId}
            page={page}
            setPage={setPage}
            itemsPerPage={itemsPerPage}
            isCollapsed={sidebarCollapsed}
            theme={theme}
          />
          
          {/* Toggle Button */}
          <Tooltip title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"} placement="right">
            <IconButton
              onClick={toggleSidebar}
              sx={{
                position: 'absolute',
                right: -20,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'background.paper',
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: 2,
                width: 40,
                height: 40,
                zIndex: 1,
                '&:hover': {
                  bgcolor: 'primary.main',
                  color: 'white',
                  transform: 'translateY(-50%) scale(1.1)',
                }
              }}
            >
              {sidebarCollapsed ? <ChevronRight /> : <ChevronLeft />}
            </IconButton>
          </Tooltip>
        </Paper>

        {/* Detail Panel */}
        <Paper 
          sx={{ 
            flex: 1, 
            position: 'relative', 
            overflow: 'hidden',
            transition: 'margin-left 0.3s ease',
          }} 
          elevation={2}
        >
          {outlet ? (
            outlet
          ) : (
            <Box sx={{ 
              position: 'absolute', 
              top: '50%', 
              left: '50%', 
              transform: 'translate(-50%, -50%)',
              textAlign: 'center'
            }}>
              <People sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h4" color="text.secondary" gutterBottom>
                Select a Creator
              </Typography>
              <Typography variant="body1" color="text.disabled" sx={{ mb: 3 }}>
                Choose a creator from the list to view their details
              </Typography>
              <Button 
                variant="outlined" 
                startIcon={<ChevronRight />}
                onClick={() => setSidebarCollapsed(false)}
                sx={{ display: sidebarCollapsed ? 'inline-flex' : 'none' }}
              >
                Show Creator List
              </Button>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
}